import { useCallback } from "react";
import { DateTime } from "luxon";

import { DataTable, TableRow } from "../../components/DataTable/DataTable";
import { useDataTable } from "../../components/DataTable/TableHook";
import { geologyAjax } from "../../ajax/GeologyAjax";
import { GeoRehandleDTO } from "../../DTOs/Geology/GeoRehandleDTO";
import { detectionSourceLabel } from "../../DTOs/Common/DetectionSource";
import { TripleShift } from "../../types/Shift";

interface ExpandedRehandleGroupRowProps {
  groupKey: string;
  selectedDate: DateTime;
  selectedShift: TripleShift;
  isCrushing: boolean;
}

// Note: the movements behind one grouped row, in the order they happened. Inference writes one row per
// confirmed movement, so this is where a person checks that a group's forty loads really were forty trips
// rather than one machine's geofence flickering.
export const ExpandedRehandleGroupRow: React.FC<ExpandedRehandleGroupRowProps> = ({
  groupKey,
  selectedDate,
  selectedShift,
  isCrushing
}) => {
  const getRows = useCallback(
    () => geologyAjax.getGeoRehandleGroupConstituents(selectedDate, selectedShift, isCrushing, groupKey),
    [selectedDate, selectedShift, isCrushing, groupKey]);

  const translateRows = useCallback((entry: GeoRehandleDTO): TableRow => {
    return {
      id: entry.geoRehandleId ?? 0,
      values: [
        {
          name: "Time",
          editable: false,
          type: "string",
          value: entry.movementTimeUtc
            ? DateTime.fromISO(entry.movementTimeUtc).toLocal().toFormat("HH:mm")
            : "-",
          tailwindClasses: "py-3 basis-[15%]",
        },
        {
          name: "Hour",
          editable: false,
          type: "integer",
          value: entry.hour ?? "-",
          tailwindClasses: "py-3 basis-[10%]",
        },
        {
          name: "Loads",
          editable: false,
          type: "integer",
          value: entry.loads ?? 0,
          tailwindClasses: "py-3 basis-[10%]",
        },
        {
          name: "Tonnes",
          editable: false,
          type: "string",
          value: entry.tonnes != null
            ? entry.tonnes.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : "-",
          tailwindClasses: "py-3 basis-[15%]",
        },
        {
          name: "Source",
          editable: false,
          type: "string",
          value: detectionSourceLabel(entry.detectionSource),
          tailwindClasses: "py-3 basis-[20%]",
        },
        {
          name: "Confidence",
          editable: false,
          type: "string",
          // Note: blank rather than 0% on a hand-entered row, which has no confidence to report.
          value: entry.confidence != null ? `${Math.round(entry.confidence * 100)}%` : "",
          tailwindClasses: "py-3 basis-[15%]",
        },
        {
          name: "Checked",
          editable: false,
          type: "string",
          value: entry.isVerified ? "Yes" : "No",
          tailwindClasses: "py-3 basis-[15%]",
        }
      ]
    }
  }, []);

  const {
    displayData,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    sortField,
    sortDirection,
    setPage,
  } = useDataTable<GeoRehandleDTO>({
    queryKey: ['geology', 'rehandleGroupConstituents', isCrushing, selectedDate.toISODate(), selectedShift, groupKey],
    fetchFn: getRows,
    translateFn: translateRows,
    initialPage: 1,
    pageSize: 10,
  });

  return (
    <DataTable
      data={displayData}
      isLoading={isLoading}
      isBackgroundFetching={isBackgroundFetching}
      error={error}
      pageNumber={pageNumber}
      setPage={setPage}
      sortField={sortField}
      sortDirection={sortDirection}
      isNestedTable={true}
    />
  );
}
